/**
 * The add-ins that may put an item in the bottom navigation bar in the place of one of its buttons
 * (MYG-154446). The list is kept here, in the Drive web code, and a change to it ships with Drive web.
 *
 * An add-in is identified by the URL of its file as the database configures it, because its name is chosen
 * by the customer (GoRoute is installed as "GoRoute" and as "GoRoute2"). The match is exact, character for
 * character: a URL that differs in case, port, query or a trailing character is not on the list.
 *
 * This keeps an ordinary add-in from using the API. It does not stop an add-in that talks to the shared
 * WebView bridge directly, because Drive has no identity for an add-in that the add-in cannot choose
 * (PostRnAddInAuthorizationModel.md, MYG-148934).
 */

/** A product. An item can open any installed add-in of the family it names. */
export type AddinFamily = "goroute" | "navigation";

const LISTED: (readonly [string, AddinFamily])[] = [
    ["https://storage.googleapis.com/geotab-addins/main/goroute/goRoute.html", "goroute"],
    ["https://storage.googleapis.com/geotab-addins/staging/goroute/goRoute.html", "goroute"],
    ["https://d2go.io/addins/goRoute/goRoute.html", "goroute"],
    ["https://attrix.gitlab.io/addins/goRoute/goRoute.html", "goroute"],
    ["https://storage.googleapis.com/geotab-addins/main/mapnavigation/mapNavigation.html", "navigation"],
    ["https://storage.googleapis.com/geotab-addins/staging/mapnavigation/mapNavigation.html", "navigation"]
];

const FAMILY_BY_URL = new Map<string, AddinFamily>(LISTED);

export function isAddinFamily(value: unknown): value is AddinFamily {
    return value === "goroute" || value === "navigation";
}

/** The family of a listed add-in, or null when the URL is not on the list. */
export function getListedFamily(url: unknown): AddinFamily | null {
    return typeof url === "string" ? FAMILY_BY_URL.get(url) ?? null : null;
}

/** The listed URLs of a family. */
export function getListedUrls(family: AddinFamily): string[] {
    return LISTED.filter(([, listedFamily]) => listedFamily === family).map(([url]) => url);
}

/** The folder of a listed add-in's file, with its trailing slash: the only place its icon may come from. */
export function getAddinFolder(listedUrl: string): string {
    return listedUrl.slice(0, listedUrl.lastIndexOf("/") + 1);
}
